import { FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import { MetadataItem } from '@aibees/elephant/hooks/types';
import { SearchBrowseParams } from '@aibees/shared/definition';
import { useCallback, useMemo } from 'react';
import { SearchFilter, SearchFilterOption } from '../..';
import { EnrichedCheckboxOption } from './types';

export function useSelectAllItemState({
  params,
  setParams,
  field,
  options,
  metadata,
  limited,
  values,
}: {
  params: Partial<SearchBrowseParams>;
  setParams: (params: Record<string, string>) => void;
  field?: string;
  options?: (SearchFilterOption & MetadataItem)[];
  metadata: SearchFilter['metadata'];
  /**
   * Selection-limit mode: the control is a "Deselect All"-only action. It never
   * switches to "Select All" (selecting everything is impossible when capped)
   * and disables once nothing is left to deselect.
   */
  limited?: boolean;
  /** Currently selected values (derived, includes the default baseline). */
  values?: string[];
}) {
  const exceptionKeys = metadata?.checkbox?.deselectAllExceptionKeys;

  const isAllSelected = useMemo(() => {
    if (params[field!]) {
      const selectedValues = params[field!]?.split(FILTER_VALUE_SEPARATOR);
      return options?.every((option) => selectedValues?.includes(option.value));
    } else {
      return options?.every((option) => option.selected);
    }
  }, [options, params, field]);

  // Limit mode disables once only the kept (exception) keys remain.
  const nothingToDeselect = useMemo(
    () => (values ?? []).every((value) => exceptionKeys?.includes(value)),
    [values, exceptionKeys]
  );

  const selectAllCheckboxOption = useMemo(() => {
    if (limited) {
      return {
        value: 'select-all',
        label: 'Deselect All',
        checked: false,
        indeterminate: false,
        disabled: nothingToDeselect,
        comingSoon: false,
        type: 'STRING',
      } as EnrichedCheckboxOption;
    }
    return {
      value: 'select-all',
      label: isAllSelected ? 'Deselect All' : 'Select All',
      checked: isAllSelected,
      indeterminate: !isAllSelected,
      disabled: false,
      comingSoon: false,
      type: 'STRING',
    } as EnrichedCheckboxOption;
  }, [limited, isAllSelected, nothingToDeselect]);

  const handleSelectAllCheckboxClick = useCallback(() => {
    const deselectToExceptions =
      options
        ?.filter((option) => exceptionKeys?.includes(option.value))
        .map((option) => option.value)
        .join(FILTER_VALUE_SEPARATOR) || '';
    const selectAll =
      options?.map((option) => option.value).join(FILTER_VALUE_SEPARATOR) || '';
    setParams({
      [field!]: limited || isAllSelected ? deselectToExceptions : selectAll,
    });
  }, [field, setParams, limited, isAllSelected, options, exceptionKeys]);

  return {
    selectAllCheckboxOption,
    handleSelectAllCheckboxClick,
  };
}
